import { Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useEffect, useState } from "react";
import { useNotification } from "providers/Notification";
import { useLogin } from "providers/Login";
import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { findOption, findOptions, isChipKeyboardRemoval, optionValuesEqual, optionsFromDict, renderOptionChips, renderOptionWithHighlight, searchTextField, sortOptionsAlphabetically } from "utils/searchableOptions";
import axios from "axios";
import { useRole } from "hooks/useRole";


interface AddUrlFilterProps {
    initialValue?: AddedUrlFilter
    filterTypeList: FilterTypeList
    onBackButtonClicked?: () => void
    //onExit is called when the user wants to go back without changing anything
    onCancel?: () => void
    onFilterAdded: (filter: AddedUrlFilter, reset: () => void) => void
    onEdit(edit: boolean): void
}

export type FilterTypeList = { [key: number]: string };

type Behavior = "Match" | "Bypass"
export interface AddedUrlFilter {
    index?: number
    type: number
    text: string
    behavior: Behavior
    contentType: Mimetypes[]
    methods: string[]
    htmlOptions?: AddedUrlFilterHTMLOpts
    detectContentType: boolean
}

interface AddedUrlFilterHTMLOpts {
    jsonSubmatchRules?: AddedUrlFilterHTMLJSONOpts
}

interface AddedUrlFilterHTMLJSONOpts {
    xpath: string[]
}

enum Mimetypes {
    FormData = "application/x-www-form-urlencoded",
    MultipartFormData = "multipart/form-data",
    JSON = "application/json",
    PDF = "application/pdf",
    OfficeDocument = "application/vnd.openxmlformats-officedocument",
    XML = "application/xml",
    CSV = "text/csv",
    HTML = "text/html",
    PlainText = "text/plain",
}

export var MimetypeName: { [key in Mimetypes]: string } = {
    "application/x-www-form-urlencoded": "Form Data",
    "multipart/form-data": "Multipart Form Data",
    "application/json": "JSON",
    "application/pdf": "PDF",
    "application/vnd.openxmlformats-officedocument": "MS Office Document",
    "application/xml": "XML",
    "text/csv": "CSV",
    "text/html": "HTML",
    "text/plain": "Plain Text"
};

export default function AddUrlFilter(props: AddUrlFilterProps) {
    const notification = useNotification();
    const login = useLogin();
    const { canEditPolicy } = useRole();
    const [contentTypes, setContentTypes] = useState<string[]>([]);
    const [methods, setMethods] = useState<string[]>([]);
    const [showAdvanced, setShowAdvanced] = useState(true);
    const [filterType, setFilterType] = useState("");
    const [filterText, setFilterText] = useState("");
    const [xpath, setXpath] = useState("");
    const [behavior, setBehavior] = useState<Behavior>("Match");
    const [validationError, setValidationError] = useState(false);
    const [detectContentType, setDetectContentType] = useState(false);
    const [editing, setEditing] = useState(false);


    const reset = () => {
        setContentTypes([]);
        setMethods([]);
        setFilterType("");
        setFilterText("");
        setBehavior("Match");
        setXpath("");
        setValidationError(false);
        setDetectContentType(false);
        setEditing(false);
        setShowAdvanced(true);
        props.onEdit(false);
    };

    useEffect(() => {
        if (!props.initialValue) {
            reset();
            return;
        }

        const getXpathInitialVal = () => {
            const xpaths = props.initialValue?.htmlOptions?.jsonSubmatchRules?.xpath;
            if (!xpaths) {
                return "";
            }

            return xpaths.join("\n");
        };

        setContentTypes(props.initialValue.contentType);
        setMethods(props.initialValue.methods);
        setFilterType(props.initialValue.type.toString());
        setFilterText(props.initialValue.text);
        setXpath(getXpathInitialVal());
        setDetectContentType(props.initialValue.detectContentType);
        setBehavior(props.initialValue.behavior);
        setEditing(true);
        setShowAdvanced(true);
        props.onEdit(true);
    }, [props.initialValue]); // eslint-disable-line react-hooks/exhaustive-deps

    const readXpath = () => {
        const list = xpath.split("\n");
        if (list.length === 1 && list[0] === "") {
            return undefined;
        }

        return {
            jsonSubmatchRules: {
                xpath: list
            }
        };
    };

    // regexTypeKey is the filter-type value whose name is "Regex", resolved from
    // the type list so it stays correct if the backend enum changes.
    const regexTypeKey = Object.entries(props.filterTypeList).find(([, name]) => name === "Regex")?.[0];

    const save = async () => {
        if (filterText === "" && filterType !== "8") {
            notification.Display({
                type: "error",
                title: "Invalid parameters",
                message: "Filter text cannot be empty"
            });
            setValidationError(true);
            return;
        }
        if (filterType === "") {
            notification.Display({
                type: "error",
                title: "Invalid parameters",
                message: "Filter type cannot be empty"
            });
            setValidationError(true);
            return;
        }

        // The filter value is only compiled as a regex when the filter type is
        // "Regex" — validate it against the engine (Go RE2) before adding.
        // NS-753
        //the validation endpoint needs policy rights, and a read-only role cannot save the result
        if (canEditPolicy && regexTypeKey !== undefined && filterType === regexTypeKey && filterText.trim() !== "") {
            const req = login.GetAxios();
            if (req) {
                try {
                    await req.post("/api/regex/validate", { regex: filterText });
                } catch (err) {
                    let message = "The regex is not valid.";
                    if (axios.isAxiosError(err) && err.response && err.response.data?.message) {
                        message = err.response.data.message;
                    }
                    notification.Display({
                        type: "error",
                        title: "Invalid regex",
                        message,
                    });
                    setValidationError(true);
                    return;
                }
            }
        }

        props.onFilterAdded({
            index: props.initialValue?.index,
            type: parseInt(filterType),
            text: filterText,
            behavior: behavior,
            contentType: contentTypes as Mimetypes[],
            methods: methods,
            htmlOptions: readXpath(),
            detectContentType: detectContentType,
        }, reset);
    };

    const filterTypeOptions = sortOptionsAlphabetically(optionsFromDict(props.filterTypeList));
    const contentTypeOptions = sortOptionsAlphabetically(optionsFromDict(MimetypeName));
    const methodOptions = ["GET", "POST", "PUT", "DELETE", "PATCH"].map(method => ({ value: method, label: method }));


    return <Stack sx={{ flex: 1, flexGrow: 0, paddingTop: "10px" }}>
        {!editing ?
            <Button
                id="add-button"
                variant="outlined"
                size="medium"
                fullWidth
                onClick={() => {
                    reset();
                    setEditing(true);
                    props.onEdit(true);
                }}
            >
                Add
            </Button>
            : <>
                <Autocomplete
                    fullWidth
                    sx={{ marginTop: "20px" }}
                    id="filter-type-select"
                    options={filterTypeOptions}
                    getOptionLabel={(option) => option.label}
                    isOptionEqualToValue={optionValuesEqual}
                    value={findOption(filterTypeOptions, filterType || null)}
                    onChange={(_, option) => setFilterType(option?.value || "")}
                    renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, false, findOption(filterTypeOptions, filterType || null)?.label)}
                    renderInput={(params) => searchTextField(params, { label: "Type", error: validationError })}
                />

                <TextField sx={{ marginTop: "20px" }} id="filter-text-input" name="text" label="Value" placeholder={filterType === "9" ? "e.g. 'namespace.appname' or 'module.namespace.appname'" : "e.g. 'domain.com' or 'hostname.another-domain.com'"} variant="outlined"
                    value={filterText}
                    onChange={x => setFilterText(x.target.value)}
                    error={validationError}
                />

                <Typography variant="h2" sx={{ marginTop: "20px", display: "flex", alignItems: "center", cursor: "pointer", ":hover": { color: "primary.main" } }}
                    onClick={() => setShowAdvanced(!showAdvanced)} >
                    {showAdvanced ? <ArrowDropUp /> : <ArrowDropDown />}<Typography component={"span"} sx={{ fontWeight: "bold", fontSize: "16px" }}>Advanced Settings</Typography>
                </Typography>
                {showAdvanced ? <>
                    <FormControl component="fieldset" sx={{ marginTop: "20px" }} error={validationError}>
                        <FormLabel component="legend">Behavior</FormLabel>
                        <RadioGroup id="behavior-radio-group" value={behavior} name="behaviour" onChange={x => setBehavior(x.target.value as Behavior)} row >
                            <FormControlLabel id="behavior-radio-option-match" value="Match" control={<Radio />} label="Match" />
                            <FormControlLabel id="behavior-radio-option-bypass" value="Bypass" control={<Radio />} label="Bypass" />
                        </RadioGroup>
                    </FormControl>

                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disableClearable
                        fullWidth
                        sx={{ marginTop: "20px" }}
                        id="select-methods"
                        options={methodOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={optionValuesEqual}
                        value={findOptions(methodOptions, methods)}
                        onChange={(event, options, reason) => {
                            if (isChipKeyboardRemoval(event, reason)) {
                                return;
                            }
                            setMethods(options.map(x => x.value));
                        }}
                        renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, methods.some(v => v === option.value))}
                        renderTags={(value, getTagProps) => renderOptionChips(value, getTagProps)}
                        renderInput={(params) => searchTextField(params, { label: "Method" })}
                    />

                    <Autocomplete
                        multiple
                        disableCloseOnSelect
                        disableClearable
                        fullWidth
                        sx={{ marginTop: "20px" }}
                        id="select-content-types"
                        options={contentTypeOptions}
                        getOptionLabel={(option) => option.label}
                        isOptionEqualToValue={optionValuesEqual}
                        value={findOptions(contentTypeOptions, contentTypes)}
                        onChange={(event, options, reason) => {
                            if (isChipKeyboardRemoval(event, reason)) {
                                return;
                            }
                            setContentTypes(options.map(x => x.value));
                        }}
                        renderOption={(liProps, option, state) => renderOptionWithHighlight(liProps, option, state, contentTypes.some(v => v === option.value))}
                        renderTags={(value, getTagProps) => renderOptionChips(value, getTagProps)}
                        renderInput={(params) => searchTextField(params, { label: "Content Type" })}
                    />

                    {contentTypes.find(x => x === Mimetypes.HTML) ?
                        <TextField
                            id="xpath-input"
                            label="HTML json elements XPath"
                            helperText="XPath for html elements containing JSON. Can have multiple xpath separated by lines."
                            multiline
                            rows={2}
                            variant="outlined"
                            sx={{ marginTop: "20px" }}
                            value={xpath}
                            error={validationError}
                            onChange={x => setXpath(x.target.value)}
                        />
                        : undefined}

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={detectContentType}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setDetectContentType(event.target.checked);
                                }}
                            />
                        }
                        label="Run content type detection"
                    />


                </> : null}
                <Box display="flex" marginBottom="15px">
                    <Button
                        id="cancel-button"
                        variant="text"
                        size="medium"
                        fullWidth
                        onClick={() => {
                            reset();
                            props.onCancel && props.onCancel();
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        id="save-button"
                        variant="outlined"
                        size="medium"
                        fullWidth
                        onClick={save}
                    >
                        Save
                    </Button>
                </Box>
            </>}

    </Stack>;
}